import { t } from "@/i18n";
import { useRef, useState } from "react";
import { Check, Palette } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

// Custom colour swatches (non-sensitive preference) — localStorage is appropriate.
const KEY = "ccs-custom-colours";
export const loadCustom = () => { try { return JSON.parse(localStorage.getItem(KEY)) || []; } catch { return []; } };
const saveCustom = (list) => localStorage.setItem(KEY, JSON.stringify(list.slice(0, 8)));

export const hslToHex = (h, s, l) => {
  s /= 100; l /= 100;
  const k = (n) => (n + h / 30) % 12;
  const a = s * Math.min(l, 1 - l);
  const f = (n) => Math.round(255 * (l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)))));
  return "#" + [f(0), f(8), f(4)].map((v) => v.toString(16).padStart(2, "0")).join("").toUpperCase();
};

const SIZE = 200;

export const ColourWheel = ({ onPick, custom, setCustom, side = "top", big }) => {
  const [open, setOpen] = useState(false);
  const [h, setH] = useState(200);
  const [s, setS] = useState(85);
  const [l, setL] = useState(50);
  const wheelRef = useRef(null);
  const dragging = useRef(false);
  const hex = hslToHex(h, s, l);

  const pickFromWheel = (e) => {
    const r = wheelRef.current.getBoundingClientRect();
    const dx = e.clientX - (r.left + r.width / 2), dy = e.clientY - (r.top + r.height / 2);
    const ang = (Math.atan2(dy, dx) * 180) / Math.PI;
    setH(Math.round((ang + 90 + 360) % 360));
    setS(Math.round(Math.min(1, Math.hypot(dx, dy) / (r.width / 2)) * 100));
  };
  const onDown = (e) => { dragging.current = true; wheelRef.current.setPointerCapture(e.pointerId); pickFromWheel(e); };
  const onMove = (e) => dragging.current && pickFromWheel(e);
  const onUp = () => { dragging.current = false; };

  const use = () => {
    const next = [hex, ...custom.filter((c) => c !== hex)].slice(0, 8);
    setCustom(next);
    saveCustom(next);
    onPick(hex);
    setOpen(false);
  };

  const rad = ((h - 90) * Math.PI) / 180, dist = (s / 100) * (SIZE / 2);
  const dot = { left: SIZE / 2 + Math.cos(rad) * dist, top: SIZE / 2 + Math.sin(rad) * dist };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button data-testid="colour-wheel-button" aria-label={t("mix_a_custom_colour")} title={t("mix_any_colour")}
          className={`shrink-0 ${big || side === "left" ? "w-16 h-16" : "w-12 h-12 sm:w-14 sm:h-14"} rounded-full border-4 border-white ring-2 ring-slate-200 hover:scale-105 transition-transform flex items-center justify-center`}
          style={{ background: "conic-gradient(#FF5252, #FFD166, #1DD1A1, #48DBFB, #845EC2, #F368E0, #FF5252)" }}>
          <span className="w-7 h-7 rounded-full bg-white/90 flex items-center justify-center"><Palette className="w-4 h-4 text-[#1E293B]" /></span>
        </button>
      </PopoverTrigger>
      <PopoverContent side={side} align="start" className="w-auto rounded-3xl border-2 border-[#1E293B] shadow-[6px_8px_0_0_#1E293B] p-4" data-testid="colour-wheel-popover">
        <p className="font-display font-semibold mb-3">{t("mix_your_own_colour")}</p>
        <div className="flex gap-5 items-start">
          <div ref={wheelRef} onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerCancel={onUp} data-testid="colour-wheel"
            className="relative rounded-full border-4 border-[#1E293B] cursor-crosshair select-none shrink-0"
            style={{ width: SIZE, height: SIZE, touchAction: "none",
              background: `radial-gradient(circle, hsl(0 0% ${l}%) 0%, transparent 70%), conic-gradient(from 0deg, hsl(0 100% ${l}%), hsl(60 100% ${l}%), hsl(120 100% ${l}%), hsl(180 100% ${l}%), hsl(240 100% ${l}%), hsl(300 100% ${l}%), hsl(360 100% ${l}%))` }}>
            <span className="absolute w-6 h-6 -ml-3 -mt-3 rounded-full border-4 border-white shadow-[0_0_0_2px_#1E293B] pointer-events-none" style={{ ...dot, backgroundColor: hex }} data-testid="colour-wheel-dot" />
          </div>
          <div className="flex flex-col gap-3 w-40">
            <div className="h-16 rounded-2xl border-4 border-[#1E293B]" style={{ backgroundColor: hex }} data-testid="colour-wheel-preview" />
            <p className="font-mono text-sm font-bold text-center" data-testid="colour-wheel-hex">{hex}</p>
            <label className="text-xs font-bold text-slate-600">{t("light_dark")}
              <input type="range" min={10} max={95} value={l} onChange={(e) => setL(+e.target.value)} data-testid="colour-wheel-lightness"
                className="w-full h-3 rounded-full appearance-none cursor-pointer mt-1"
                style={{ background: `linear-gradient(to right, hsl(${h} ${s}% 10%), hsl(${h} ${s}% 50%), hsl(${h} ${s}% 95%))` }} />
            </label>
            <button onClick={use} className="btn-pop btn-red !py-2.5 !px-4 text-sm" data-testid="colour-wheel-use-button"><Check className="w-4 h-4" /> {t("use_this_colour")}</button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};
